import { canonicalizeKnownBrandText, ValidationError } from "./recordValidation.mjs";
import { findProjectForOutcome } from "./workModel.mjs";

export const HANDOFF_JOURNAL_KEY = "groot-handoff-journal";
const TYPES = new Set(["schedule_block", "task", "project", "outcome", "content", "decision", "delegation", "state_update", "idea", "commitment"]);
const hash = value => { let h = 2166136261; for (let i = 0; i < value.length; i++) { h ^= value.charCodeAt(i); h = Math.imul(h, 16777619); } return (h >>> 0).toString(36); };
const stableId = (prefix, value) => `${prefix}-${hash(value)}`;
const isoDate = /^\d{4}-\d{2}-\d{2}$/;
const time = /^([01]\d|2[0-3]):[0-5]\d$/;

export function parseProposalArray(raw) {
  if (Array.isArray(raw)) return raw;
  const cleaned = String(raw || "").replace(/^```(?:json)?\s*/i, "").replace(/\s*```$/, "").trim();
  const parsed = JSON.parse(cleaned);
  if (!Array.isArray(parsed)) throw new ValidationError("Close Meeting response must be a JSON array");
  return parsed;
}

export function isContextOnlyStatement(value) {
  const text = `${value?.title || ""} ${value?.details || ""}`.toLowerCase();
  return /\b(protected|spacious|buffer|context only|no executable work)\b/.test(text) && !/\b(draft|write|record|review|send|publish|create|build|schedule|edit|approve|prepare|complete|deliver)\b/.test(text);
}

export function normalizeHandoffProposals(raw, { sessionId, maxItems = 64 } = {}) {
  const items = parseProposalArray(raw);
  if (items.length > maxItems) throw new ValidationError(`Close Meeting returned ${items.length} proposals; maximum is ${maxItems}. Nothing was truncated.`);
  return items.map((source, index) => {
    const type = String(source.type || "").trim();
    if (!TYPES.has(type)) throw new ValidationError(`Unsupported proposal type at index ${index}: ${type}`);
    const proposal = { ...source, type };
    for (const key of ["title", "details", "outcomeTitle", "projectTitle"]) if (proposal[key]) proposal[key] = canonicalizeKnownBrandText(String(proposal[key]).trim());
    if (!proposal.title && type !== "state_update") throw new ValidationError(`Proposal ${index + 1} is missing a title`);
    if (type === "schedule_block") {
      if (!isoDate.test(proposal.scheduleDate || "")) throw new ValidationError(`Schedule proposal ${index + 1} requires YYYY-MM-DD scheduleDate`);
      if (proposal.startTime && !time.test(proposal.startTime)) throw new ValidationError(`Schedule proposal ${index + 1} has an invalid startTime`);
      if (proposal.endTime && !time.test(proposal.endTime)) throw new ValidationError(`Schedule proposal ${index + 1} has an invalid endTime`);
      proposal.taskTitles = [...new Set((proposal.taskTitles || []).map(title => canonicalizeKnownBrandText(String(title).trim())).filter(Boolean))];
      if (!proposal.taskTitles.length) throw new ValidationError(`Schedule proposal ${index + 1} requires taskTitles`);
    }
    if (["task", "schedule_block"].includes(type) && isContextOnlyStatement(proposal)) throw new ValidationError(`Context-only statement cannot become executable work: ${proposal.title}`);
    const identity = JSON.stringify([sessionId || "session", type, proposal.scheduleDate || "", proposal.startTime || "", proposal.title || "", proposal.taskTitles || [], index]);
    proposal.id = proposal.id || stableId("proposal", identity);
    proposal.status = "pending";
    return proposal;
  });
}

export function createHandoffOperation(sessionId, proposals) {
  return { id: stableId("handoff", JSON.stringify([sessionId, proposals.map(p => p.id)])), sessionId, proposalIds: proposals.map(p => p.id), status: "staged" };
}

const upsert = (records, record) => { const index = records.findIndex(item => String(item.id) === String(record.id)); return index < 0 ? [...records, record] : records.map((item, i) => i === index ? { ...item, ...record } : item); };
const journalUpsert = (journal, entry) => upsert(journal, entry);

export async function commitHandoffProposal({ repository, operationId, proposal, projects = [], now = () => new Date().toISOString(), failAfterStage }) {
  const journal = await repository.getStore(HANDOFF_JOURNAL_KEY);
  const existing = journal.find(entry => entry.operationId === operationId && entry.proposalId === proposal.id);
  if (existing?.status === "committed") return existing;
  const updateJournal = async (status, extra = {}) => {
    const latest = await repository.getStore(HANDOFF_JOURNAL_KEY);
    const entry = { operationId, proposalId: proposal.id, type: proposal.type, status, updatedAt: now(), ...extra };
    await repository.setStore(HANDOFF_JOURNAL_KEY, journalUpsert(latest, entry));
    return entry;
  };
  await updateJournal(existing?.status || "staged");

  if (!['task','schedule_block'].includes(proposal.type)) throw new ValidationError(`Reliable handoff is not yet configured for ${proposal.type}`);
  let tasks = await repository.getRecords("task");
  const project = projects.find(item => String(item.id) === String(proposal.projectId)) || findProjectForOutcome(projects, proposal.outcomeId, proposal.outcomeTitle);
  const taskTitles = proposal.type === "task" ? [proposal.title] : proposal.taskTitles;
  const taskIds = [];
  for (let index = 0; index < taskTitles.length; index++) {
    const title = taskTitles[index], id = stableId("task", `${operationId}:${proposal.id}:${index}:${title}`);
    taskIds.push(id);
    tasks = upsert(tasks, {
      id, name: title, status: "not started", category: "business", priority: proposal.priority || "medium", energy: proposal.energy || "medium",
      plannedDate: proposal.scheduleDate || proposal.plannedDate || "", dueDate: proposal.dueDate || "", notes: proposal.details || "",
      ownerNow: proposal.owner || "K", founderRequired: (proposal.owner || "K") === "K", workType: proposal.workType || "create", timeframe: "weekly",
      projectId: project?.id || proposal.projectId || "", projectTitle: project?.name || proposal.projectTitle || "",
      outcomeId: proposal.outcomeId || project?.outcomeId || "", outcomeTitle: proposal.outcomeTitle || project?.outcomeTitle || "",
      sourceProposalId: proposal.id, handoffOperationId: operationId, createdAt: now(),
    });
  }
  await repository.setRecords("task", tasks, { strict: true });
  await updateJournal("tasks_written", { taskIds });
  if (failAfterStage === "tasks_written") throw Object.assign(new Error("Simulated partial failure after Task persistence"), { code: "SIMULATED_PARTIAL_FAILURE" });

  let schedule = await repository.getRecords("schedule_block");
  let blockId = null;
  if (proposal.type === "schedule_block") {
    blockId = stableId("block", `${operationId}:${proposal.id}`);
    schedule = upsert(schedule, {
      id: blockId, title: proposal.title, date: proposal.scheduleDate, scheduleDate: proposal.scheduleDate, startTime: proposal.startTime || "", endTime: proposal.endTime || "",
      details: proposal.details || "", status: "approved", taskIds, projectId: project?.id || proposal.projectId || "", projectTitle: project?.name || proposal.projectTitle || "",
      outcomeId: proposal.outcomeId || project?.outcomeId || "", outcomeTitle: proposal.outcomeTitle || project?.outcomeTitle || "",
      owner: proposal.owner || "K", sourceProposalId: proposal.id, handoffOperationId: operationId, approvedAt: now(),
    });
    await repository.setRecords("schedule_block", schedule, { strict: true });
    await updateJournal("schedule_written", { taskIds, blockId });
  }

  const persistedTasks = await repository.getRecords("task");
  const persistedSchedule = await repository.getRecords("schedule_block");
  if (taskIds.some(id => !persistedTasks.some(task => String(task.id) === id))) throw new Error("Relationship verification failed: a Task was not persisted");
  if (blockId) {
    const block = persistedSchedule.find(item => String(item.id) === blockId);
    if (!block || block.taskIds.some(id => !persistedTasks.some(task => String(task.id) === String(id)))) throw new Error("Relationship verification failed: Schedule → Task links are unresolved");
  }
  return updateJournal("committed", { taskIds, blockId, committedAt: now() });
}
